import { assertUnreachable, isDefined } from '@sgme/fp';
import { v4 } from 'uuid';

import { ALL_SIGNALR_TOPICS, type SignalRListener, type SignalRTopic } from '@/api/signalR/messages/signalRtypes.ts';
import type { StartMessage, WorkerMessage } from '@/api/signalR/worker/messages/worker-messages.ts';
import type {
  ConnectionStatusUpdate,
  SignalRMessageReceived,
  WorkerSentMessage,
} from '@/api/signalR/worker/messages/WorkerSentMessage.ts';
import { logger } from '@/components/SgwtBootstrap/logger.ts';

import NewWorker from './worker/sharedWorker.ts?sharedworker';

export type SignalRConnectionListener = (status: ConnectionStatusUpdate) => void;

export type HeartbeatTimeoutListener = (rfsId: string) => void;

type TopicListeners = { [topic in SignalRTopic]: SignalRListener<any>[] };
// TODO: do we really need a singleton here?
let signalRConnector: SignalRConnector | undefined;

export function getSignalRConnector(tabId: string = v4()): SignalRConnector {
  if (signalRConnector) {
    return signalRConnector;
  }

  const streamingWorker = new NewWorker({ name: 'FX streaming worker' });
  const connector = new SignalRConnector(tabId, streamingWorker);
  signalRConnector = connector;

  window.addEventListener('beforeunload', (_event: BeforeUnloadEvent) => {
    logger.logInformation('Stopping signalRConnector before unload');
    connector.stop();
  });

  streamingWorker.port.onmessage = (event: MessageEvent) => {
    const workerMessage = event.data as unknown as WorkerSentMessage;

    switch (workerMessage.type) {
      case 'signalRMessageReceived': {
        connector.notifySignalRListeners(workerMessage);
        break;
      }
      case 'statusUpdate': {
        connector.updateConnectionStatus(workerMessage);
        break;
      }
      case 'heartbeatTimeout': {
        connector.notifyHeartbeatTimeout(workerMessage.streamId);
        break;
      }
      case 'tokenRequested': {
        // Nothing produces this message yet: no automatic token renewal is wired up.
        logger.logWarning('SharedWorker requested a fresh token but no renewal handler is registered');
        break;
      }
      default:
        return assertUnreachable(
          workerMessage,
          `Unknown message type received from worker: ${JSON.stringify(workerMessage)}`,
        );
    }
  };

  return signalRConnector;
}

class SignalRConnector {
  private listeners: TopicListeners = {} as TopicListeners;
  signalRConnectionStatusListeners = new Set<SignalRConnectionListener>();
  private heartbeatTimeoutListeners: Map<string, Set<HeartbeatTimeoutListener>> = new Map();
  lastSignalRConnectionStatusMessage: ConnectionStatusUpdate = {
    type: 'statusUpdate',
    status: 'idle',
  };

  constructor(
    private tabId: string,
    private streamingWorker: SharedWorker,
  ) {
    for (const topic of ALL_SIGNALR_TOPICS) {
      this.listeners[topic] = [];
    }
  }

  refreshAccessToken(token: string): void {
    this.post({ type: 'refreshToken', sgConnectToken: token, tabId: this.tabId });
  }
  onConnectionStatusUpdate(listener: SignalRConnectionListener): void {
    this.signalRConnectionStatusListeners.add(listener);
    this.notifyForStatusUpdate(listener, this.lastSignalRConnectionStatusMessage);
  }

  start(params: Omit<StartMessage, 'type' | 'tabId'>): void {
    this.post({ type: 'start', tabId: this.tabId, ...params });
  }
  pull(delta: number) {
    this.post({ type: 'pull', tabId: this.tabId, deltaMs: delta });
  }
  beginPullLoop() {
    let lastTimestamp: number;
    const onUpdate = (timestamp: DOMHighResTimeStamp) => {
      const delta = isDefined(lastTimestamp) ? timestamp - lastTimestamp : 0;
      this.pull(delta);
      lastTimestamp = timestamp;
      requestAnimationFrame(onUpdate);
    };
    requestAnimationFrame(onUpdate);
  }

  stop(): void {
    logger.logInformation('SignalRConnector stopped');
    this.post({ type: 'stop', tabId: this.tabId });
  }
  on<K extends SignalRTopic>(topic: K, listener: SignalRListener<K>): void {
    this.listeners[topic].push(listener);
  }
  off<K extends SignalRTopic>(topic: K, listener: SignalRListener<K>): void {
    const index = this.listeners[topic].indexOf(listener);
    if (index !== -1) {
      this.listeners[topic].splice(index, 1);
    }
  }

  private post(message: WorkerMessage) {
    this.streamingWorker.port.postMessage(message);
  }

  notifySignalRListeners(workerMessage: SignalRMessageReceived) {
    const { topic, payload } = workerMessage;
    for (const listener of this.listeners[topic]) {
      try {
        listener(...payload);
      } catch (error) {
        logger.logError('SignalR listener threw an error {error_s}', JSON.stringify(error));
      }
    }
  }

  notifyHeartbeatTimeout(rfsId: string) {
    const listeners = this.heartbeatTimeoutListeners.get(rfsId);
    if (!listeners) {
      return;
    }
    for (const listener of listeners) {
      try {
        listener(rfsId);
      } catch (error) {
        logger.logError(
          '[SignalRConnector] heartbeat timeout listener threw an error {error_s}',
          JSON.stringify(error),
        );
      }
    }
  }

  onHeartbeatTimeout(rfsId: string, listener: HeartbeatTimeoutListener): void {
    const listeners = this.heartbeatTimeoutListeners.get(rfsId) ?? new Set<HeartbeatTimeoutListener>();
    listeners.add(listener);
    this.heartbeatTimeoutListeners.set(rfsId, listeners);
  }

  offHeartbeatTimeout(rfsId: string, listener: HeartbeatTimeoutListener): void {
    const listeners = this.heartbeatTimeoutListeners.get(rfsId);
    if (!listeners) {
      return;
    }
    listeners.delete(listener);
    if (listeners.size === 0) {
      this.heartbeatTimeoutListeners.delete(rfsId);
    }
  }

  updateConnectionStatus(message: ConnectionStatusUpdate) {
    const statusChanged = JSON.stringify(this.lastSignalRConnectionStatusMessage) !== JSON.stringify(message);
    this.lastSignalRConnectionStatusMessage = message;
    if (statusChanged) {
      for (const listener of this.signalRConnectionStatusListeners) {
        this.notifyForStatusUpdate(listener, message);
      }
    }
  }

  private notifyForStatusUpdate(listener: SignalRConnectionListener, message: ConnectionStatusUpdate) {
    try {
      listener(message);
    } catch (error) {
      logger.logError('[SignalRConnector] connection status listener threw an error {error_s}', error);
    }
  }
}
